import { useAuth } from "@/shared/auth/AuthContext";
import { useServerListQuery } from "@/shared/list/useServerListQuery";
import { useRealtimeNotifyConnected } from "@/modules/sys/realtime/realtimeNotifyStatus";
import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCategory,
  createCategoryDetail,
  createPlant,
  createRole,
  createShift,
  createUser,
  deleteCategory,
  deleteCategoryDetail,
  deleteMobileAppRelease,
  deletePlant,
  deleteRole,
  deleteUser,
  getCategory,
  getMobileAppRelease,
  createMobileAppRelease,
  listMobileAppReleases,
  listMobileAppPlatforms,
  publishMobileAppRelease,
  retireMobileAppRelease,
  updateMobileAppRelease,
  listCategoryDetailsByCode,
  getRole,
  getUser,
  listAudit,
  listEmailLogs,
  listInAppNotifications,
  listNotificationCatalog,
  listNotificationPreferences,
  listNotificationRoleDefaults,
  listPermissions,
  listAppSettings,
  listCategories,
  listNumberingRules,
  listPlants,
  listProductionLines,
  listAllRoles,
  listRoles,
  listShifts,
  listUsers,
  lockUser,
  markAllInAppNotificationsRead,
  markInAppNotificationRead,
  resetUserPassword,
  setNotificationEventEnabled,
  setNotificationGroupEnabled,
  setPlantStatus,
  setRolePermissions,
  setShiftStatus,
  unlockUser,
  updateCategory,
  updateCategoryDetail,
  updatePlant,
  updateRole,
  updateUser,
  upsertNotificationPreferences,
} from "@/modules/sys/api/sysApi";
import type {
  CreateCategoryDetailInput,
  CreateCategoryInput,
  CreatePlantInput,
  CreateRoleInput,
  CreateShiftInput,
  CreateUserInput,
  UpdateCategoryDetailInput,
  UpdateCategoryInput,
  NotificationPreference,
  UpdatePlantInput,
  UpdateRoleInput,
  UpdateUserInput,
  UpsertMobileAppReleaseInput,
} from "@/modules/sys/model/types";

export function usePlantsListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "plants"],
    fetcher: (params) => listPlants(http, params),
    filtersInitialOpen: true,
  });
}

export function usePlantsLookupQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "plants", "lookup"],
    queryFn: () =>
      listPlants(http, {
        page: 1,
        pageSize: 100,
        q: "",
        status: "Active",
        filters: {},
      }),
  });
}

export function useProductionLinesLookupQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "production-lines", "lookup"],
    queryFn: () => listProductionLines(http),
  });
}

export function useShiftsListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "shifts"],
    fetcher: (params) => listShifts(http, params),
    filtersInitialOpen: true,
  });
}

export function useNumberingListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "numbering"],
    fetcher: (params) => listNumberingRules(http, params),
    filtersInitialOpen: true,
  });
}

export function useAppSettingsListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "settings"],
    fetcher: (params) => listAppSettings(http, params),
    filtersInitialOpen: true,
  });
}

export function useUsersListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "users"],
    fetcher: (params) => listUsers(http, params),
    filterKeys: ["roleCode"],
    filtersInitialOpen: true,
  });
}

/** Users assigned to a role (detail panel), one server page at a time. */
export function useUsersByRoleQuery(
  roleCode: string | null,
  page: number,
  pageSize: number,
) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "users", "by-role", roleCode, page, pageSize],
    queryFn: () =>
      listUsers(http, {
        page,
        pageSize,
        q: "",
        status: "all",
        filters: { roleCode: roleCode! },
      }),
    enabled: Boolean(roleCode),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[3] === roleCode ? previousData : undefined,
  });
}

/** Lookup all users for assign-to-role picker. */
export function useUsersLookupQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "users", "lookup"],
    queryFn: () =>
      listUsers(http, {
        page: 1,
        pageSize: 200,
        q: "",
        status: "Active",
        filters: {},
      }),
  });
}

export function useRolesListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "roles"],
    fetcher: (params) => listRoles(http, params),
  });
}

export function useAuditListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "audit"],
    fetcher: (params) => listAudit(http, params),
    filterKeys: ["entityType"],
    filtersInitialOpen: true,
  });
}

export function useEmailLogsQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "email-logs"],
    fetcher: (params) => listEmailLogs(http, params),
    filtersInitialOpen: true,
  });
}

/** Entity-scoped audit for detail tabs (T4). */
export function useEntityAuditQuery(entityType: string | null, entityId: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "audit", "entity", entityType, entityId],
    queryFn: () =>
      listAudit(http, {
        page: 1,
        pageSize: 50,
        q: "",
        status: "all",
        filters: {
          entityType: entityType!,
          entityId: entityId!,
        },
      }),
    enabled: Boolean(entityType && entityId),
  });
}

/** All non-deleted roles for the role tree and assignment pickers. */
export function useRolesLookupQuery(options?: { enabled?: boolean }) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "roles", "lookup"],
    queryFn: () => listAllRoles(http),
    enabled: options?.enabled ?? true,
  });
}

export function usePermissionsCatalogQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "permissions"],
    queryFn: () => listPermissions(http),
  });
}

export function useUserQuery(id: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "users", id],
    queryFn: () => getUser(http, id!),
    enabled: Boolean(id),
  });
}

export function useRoleQuery(id: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "roles", id],
    queryFn: () => getRole(http, id!),
    enabled: Boolean(id),
  });
}

export function useCreatePlantMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePlantInput) => createPlant(http, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "plants"] });
      void qc.invalidateQueries({ queryKey: ["sys", "shifts"] });
    },
  });
}

export function useUpdatePlantMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePlantInput }) =>
      updatePlant(http, id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "plants"] });
    },
  });
}

export function useDeletePlantMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePlant(http, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "plants"] });
    },
  });
}

export function useSetPlantStatusMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status: string } }) =>
      setPlantStatus(http, id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "plants"] });
    },
  });
}

export function useCreateShiftMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateShiftInput) => createShift(http, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "shifts"] });
    },
  });
}

export function useSetShiftStatusMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status: string } }) =>
      setShiftStatus(http, id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "shifts"] });
    },
  });
}

function invCategories(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: ["sys", "categories"] });
  if (id) void qc.invalidateQueries({ queryKey: ["sys", "categories", "detail", id] });
}

export function useCategoriesListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "categories"],
    fetcher: (params) => listCategories(http, params),
    filtersInitialOpen: true,
    defaultPageSize: 10,
  });
}

export function useCategoryDetailsByCodeQuery(code: string) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "categories", "by-code", code, "details"],
    queryFn: () => listCategoryDetailsByCode(http, code),
    enabled: Boolean(code.trim()),
  });
}

export function useCategoryQuery(id: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "categories", "detail", id],
    queryFn: () => getCategory(http, id!),
    enabled: Boolean(id),
  });
}

export function useCreateCategoryMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCategoryInput) => createCategory(http, body),
    onSuccess: (row) => invCategories(qc, row.id),
  });
}

export function useUpdateCategoryMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCategoryInput }) =>
      updateCategory(http, id, body),
    onSuccess: (row) => invCategories(qc, row.id),
  });
}

export function useDeleteCategoryMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCategory(http, id),
    onSuccess: (_void, id) => invCategories(qc, id),
  });
}

function invMobileReleases(qc: ReturnType<typeof useQueryClient>, id?: string) {
  void qc.invalidateQueries({ queryKey: ["sys", "mobile-app-releases"] });
  void qc.invalidateQueries({ queryKey: ["sys", "mobile-app-platforms"] });
  if (id) void qc.invalidateQueries({ queryKey: ["sys", "mobile-app-releases", "detail", id] });
}

export function useMobileAppPlatformsQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "mobile-app-platforms"],
    queryFn: () => listMobileAppPlatforms(http),
  });
}

export function useMobileAppReleaseHistoryQuery(platform: string | null) {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "mobile-app-releases", "history", platform ?? ""],
    fetcher: (params) =>
      listMobileAppReleases(http, {
        ...params,
        filters: platform
          ? { ...params.filters, platform, excludeCurrent: "true" }
          : params.filters,
      }),
    enabled: Boolean(platform),
    filtersInitialOpen: true,
    defaultPageSize: 10,
  });
}

export function useMobileAppReleasesListQuery() {
  const { http } = useAuth();
  return useServerListQuery({
    queryKey: ["sys", "mobile-app-releases"],
    fetcher: (params) => listMobileAppReleases(http, params),
    filterKeys: ["platform"],
    filtersInitialOpen: true,
    defaultPageSize: 10,
  });
}

export function useMobileAppReleaseQuery(id: string | null) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "mobile-app-releases", "detail", id],
    queryFn: () => getMobileAppRelease(http, id!),
    enabled: Boolean(id),
  });
}

export function useCreateMobileAppReleaseMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertMobileAppReleaseInput) => createMobileAppRelease(http, body),
    onSuccess: (row) => invMobileReleases(qc, row.id),
  });
}

export function useUpdateMobileAppReleaseMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertMobileAppReleaseInput }) =>
      updateMobileAppRelease(http, id, body),
    onSuccess: (row) => invMobileReleases(qc, row.id),
  });
}

export function useDeleteMobileAppReleaseMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMobileAppRelease(http, id),
    onSuccess: (_void, id) => invMobileReleases(qc, id),
  });
}

export function usePublishMobileAppReleaseMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => publishMobileAppRelease(http, id),
    onSuccess: (row) => invMobileReleases(qc, row.id),
  });
}

export function useRetireMobileAppReleaseMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => retireMobileAppRelease(http, id),
    onSuccess: (row) => invMobileReleases(qc, row.id),
  });
}

export function useCreateCategoryDetailMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      body,
    }: {
      categoryId: string;
      body: CreateCategoryDetailInput;
    }) => createCategoryDetail(http, categoryId, body),
    onSuccess: (_row, vars) => invCategories(qc, vars.categoryId),
  });
}

export function useUpdateCategoryDetailMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      detailId,
      body,
    }: {
      categoryId: string;
      detailId: string;
      body: UpdateCategoryDetailInput;
    }) => updateCategoryDetail(http, categoryId, detailId, body),
    onSuccess: (_row, vars) => invCategories(qc, vars.categoryId),
  });
}

export function useDeleteCategoryDetailMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoryId,
      detailId,
    }: {
      categoryId: string;
      detailId: string;
    }) => deleteCategoryDetail(http, categoryId, detailId),
    onSuccess: (_void, vars) => invCategories(qc, vars.categoryId),
  });
}

export function useCreateUserMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserInput) => createUser(http, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "users"] });
    },
  });
}

export function useUpdateUserMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserInput }) =>
      updateUser(http, id, body),
    onSuccess: (data) => {
      qc.setQueryData(["sys", "users", data.id], data);
      void qc.invalidateQueries({ queryKey: ["sys", "users"] });
    },
  });
}

export function useDeleteUserMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(http, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "users"] });
    },
  });
}

export function useLockUserMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lockUser(http, id),
    onSuccess: (data) => {
      qc.setQueryData(["sys", "users", data.id], data);
      void qc.invalidateQueries({ queryKey: ["sys", "users"] });
    },
  });
}

export function useUnlockUserMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unlockUser(http, id),
    onSuccess: (data) => {
      qc.setQueryData(["sys", "users", data.id], data);
      void qc.invalidateQueries({ queryKey: ["sys", "users"] });
    },
  });
}

export function useResetUserPasswordMutation() {
  const { http } = useAuth();
  return useMutation({
    mutationFn: (id: string) => resetUserPassword(http, id),
  });
}

export function useDeleteRoleMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRole(http, id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: ["sys", "roles", id] });
      void qc.invalidateQueries({ queryKey: ["sys", "roles", "lookup"] });
    },
  });
}

export function useCreateRoleMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoleInput) => createRole(http, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "roles"] });
    },
  });
}

export function useUpdateRoleMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRoleInput }) =>
      updateRole(http, id, body),
    onSuccess: (data) => {
      qc.setQueryData(["sys", "roles", data.id], data);
      void qc.invalidateQueries({ queryKey: ["sys", "roles"] });
    },
  });
}

export function useSetRolePermissionsMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permissionCodes }: { id: string; permissionCodes: string[] }) =>
      setRolePermissions(http, id, permissionCodes),
    onSuccess: (data) => {
      qc.setQueryData(["sys", "roles", data.id], data);
      void qc.invalidateQueries({ queryKey: ["sys", "roles"] });
    },
  });
}

export function useNotificationPreferencesQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "notify-prefs"],
    queryFn: () => listNotificationPreferences(http),
  });
}

export function useUpsertNotificationPreferencesMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: NotificationPreference[]) =>
      upsertNotificationPreferences(http, items),
    onSuccess: (data) => {
      qc.setQueryData(["sys", "notify-prefs"], data);
    },
  });
}

export function useNotificationCatalogQuery(group?: string) {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "notify-catalog", group ?? "all"],
    queryFn: () => listNotificationCatalog(http, group),
  });
}

export function useNotificationRoleDefaultsQuery() {
  const { http } = useAuth();
  return useQuery({
    queryKey: ["sys", "notify-role-defaults"],
    queryFn: () => listNotificationRoleDefaults(http),
  });
}

export function useSetNotificationEventEnabledMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventType,
      isEnabled,
    }: {
      eventType: string;
      isEnabled: boolean;
    }) => setNotificationEventEnabled(http, eventType, isEnabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "notify-catalog"] });
    },
  });
}

export function useSetNotificationGroupEnabledMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupCode,
      isEnabled,
    }: {
      groupCode: string;
      isEnabled: boolean;
    }) => setNotificationGroupEnabled(http, groupCode, isEnabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "notify-catalog"] });
    },
  });
}

export const NOTIFY_INBOX_PAGE_SIZE = 20;

export function useInAppNotificationsQuery(opts?: {
  take?: number;
  unreadOnly?: boolean;
  /** Poll interval when online (N2 bell). `false` = no poll. Paused while SignalR connected. */
  refetchIntervalMs?: number | false;
}) {
  const { http } = useAuth();
  const realtimeConnected = useRealtimeNotifyConnected();
  const requestedInterval = opts?.refetchIntervalMs ?? false;
  return useQuery({
    queryKey: ["sys", "notifications", opts?.take ?? 50, opts?.unreadOnly ?? false],
    queryFn: () => listInAppNotifications(http, opts),
    refetchInterval: realtimeConnected ? false : requestedInterval,
  });
}

/** Field inbox — server pages + swipe load-more (IntersectionObserver). */
export function useInAppNotificationsInfiniteQuery(opts?: {
  unreadOnly?: boolean;
}) {
  const { http } = useAuth();
  const unreadOnly = opts?.unreadOnly ?? false;
  return useInfiniteQuery({
    queryKey: ["sys", "notifications", "infinite", unreadOnly],
    queryFn: ({ pageParam }) =>
      listInAppNotifications(http, {
        take: NOTIFY_INBOX_PAGE_SIZE,
        skip: pageParam,
        unreadOnly: unreadOnly || undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < NOTIFY_INBOX_PAGE_SIZE) return undefined;
      return allPages.reduce((n, p) => n + p.length, 0);
    },
  });
}

export function useMarkNotificationReadMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markInAppNotificationRead(http, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "notifications"] });
    },
  });
}

export function useMarkAllNotificationsReadMutation() {
  const { http } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllInAppNotificationsRead(http),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sys", "notifications"] });
    },
  });
}
